import React from "react";
import {
  BracketMatch,
  BracketRound,
  SingleEliminationTournament,
} from "../types/tournament";
import { Trophy, Users, ArrowRight } from "lucide-react";

interface BracketDisplayProps {
  tournament: SingleEliminationTournament;
  onMatchClick: (match: BracketMatch) => void;
}

const BracketDisplay: React.FC<BracketDisplayProps> = ({
  tournament,
  onMatchClick,
}) => {
  const getMatchStatus = (match: BracketMatch) => {
    if (match.isCompleted) return "completed";
    if (match.team1 && match.team2) return "ready";
    if (match.team1 || match.team2) return "waiting";
    return "empty";
  };

  const getMatchColor = (match: BracketMatch) => {
    const status = getMatchStatus(match);
    switch (status) {
      case "completed":
        return "bg-green-100 border-green-300";
      case "ready":
        return "bg-blue-100 border-blue-300";
      case "waiting":
        return "bg-yellow-100 border-yellow-300";
      default:
        return "bg-gray-100 border-gray-300";
    }
  };

  const renderMatch = (match: BracketMatch, roundIndex: number) => {
    const status = getMatchStatus(match);
    const isCurrentRound = roundIndex + 1 === tournament.currentRound;

    return (
      <div
        key={match.id}
        className={`relative ${
          roundIndex < tournament.rounds.length - 1 ? "mb-8" : ""
        }`}
      >
        <div
          className={`border-2 rounded-lg p-3 cursor-pointer transition-all duration-200 hover:shadow-md ${getMatchColor(
            match
          )} ${
            isCurrentRound && status === "ready" ? "ring-2 ring-blue-500" : ""
          }`}
          onClick={() => onMatchClick(match)}
        >
          {/* Match Header */}
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-medium text-gray-600">
              Match {match.matchNumber}
            </span>
            {match.isCompleted && <Trophy className="w-4 h-4 text-green-600" />}
          </div>

          {/* Team 1 */}
          <div className="mb-2">
            <div
              className={`text-sm font-medium p-2 rounded ${
                match.winner?.id === match.team1?.id
                  ? "bg-green-200 text-green-800"
                  : match.loser?.id === match.team1?.id
                  ? "bg-red-200 text-red-800"
                  : "bg-white"
              }`}
            >
              {match.team1 ? (
                <div>
                  <div className="font-semibold">{match.team1.name}</div>
                  <div className="text-xs text-gray-600">
                    {match.team1.player1} & {match.team1.player2}
                  </div>
                  {match.result && (
                    <div className="text-xs font-bold">
                      {match.result.team1Score}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-400 italic">TBD</div>
              )}
            </div>
          </div>

          {/* VS */}
          <div className="text-center text-xs text-gray-500 mb-2">VS</div>

          {/* Team 2 */}
          <div>
            <div
              className={`text-sm font-medium p-2 rounded ${
                match.winner?.id === match.team2?.id
                  ? "bg-green-200 text-green-800"
                  : match.loser?.id === match.team2?.id
                  ? "bg-red-200 text-red-800"
                  : "bg-white"
              }`}
            >
              {match.team2 ? (
                <div>
                  <div className="font-semibold">{match.team2.name}</div>
                  <div className="text-xs text-gray-600">
                    {match.team2.player1} & {match.team2.player2}
                  </div>
                  {match.result && (
                    <div className="text-xs font-bold">
                      {match.result.team2Score}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-400 italic">TBD</div>
              )}
            </div>
          </div>

          {/* Bye indicator */}
          {match.isBye && (
            <div className="mt-2 text-center">
              <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded">
                Bye
              </span>
            </div>
          )}

          {/* Status indicator */}
          {status === "ready" && !match.isBye && (
            <div className="mt-2 text-center">
              <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                Ready to Play
              </span>
            </div>
          )}
        </div>

        {/* Connection line to next round */}
        {roundIndex < tournament.rounds.length - 1 && (
          <div className="absolute top-1/2 -right-4 w-8 h-0.5 bg-gray-300 transform -translate-y-1/2"></div>
        )}
      </div>
    );
  };

  const renderRound = (round: BracketRound, roundIndex: number) => {
    return (
      <div key={round.roundNumber} className="flex flex-col">
        {/* Round Header */}
        <div className="text-center mb-4">
          <h3 className="text-lg font-bold text-gray-900">{round.roundName}</h3>
          <p className="text-sm text-gray-600">
            {round.matches.filter((m) => m.isCompleted).length} of{" "}
            {round.matches.length} matches complete
          </p>
        </div>

        {/* Matches */}
        <div className="space-y-4">
          {round.matches.map((match) => renderMatch(match, roundIndex))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Tournament Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          🏆 {tournament.name}
        </h2>
        <p className="text-gray-600">
          Single Elimination Bracket • {tournament.teams.length} Teams • Round{" "}
          {tournament.currentRound} of {tournament.totalRounds}
        </p>

        {/* Tournament Progress */}
        <div className="mt-4">
          <div className="flex justify-center items-center space-x-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {tournament.champion ? "🏆" : "..."}
              </div>
              <div className="text-sm text-gray-600">Champion</div>
            </div>
            {tournament.champion && (
              <>
                <ArrowRight className="w-6 h-6 text-gray-400" />
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {tournament.champion.name}
                  </div>
                  <div className="text-sm text-gray-600">
                    {tournament.champion.player1} &{" "}
                    {tournament.champion.player2}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bracket Display */}
      <div className="overflow-x-auto">
        <div className="flex space-x-8 min-w-max p-4">
          {tournament.rounds.map((round, index) => (
            <div key={round.roundNumber} className="flex-shrink-0">
              {renderRound(round, index)}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
            <span>Completed</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
            <span>Ready to Play</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded"></div>
            <span>Waiting for Opponent</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></div>
            <span>Empty</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BracketDisplay;
